import tw from 'twin.macro';
import React from 'react';
import _isEmpty from 'lodash-es/isEmpty';
import { format } from '@teetsh/app/src/utils/time/format';
import { Gender } from '@teetsh/app/src/types/gender';
import clsx from 'clsx';
import { Teacher } from '@teetsh/app/src/containers/Livrets/types/livret';
import { AddAdditionalTeachers } from '@teetsh/app/src/containers/Livrets/LivretEval/LivretEleve/components/LivretEleve/FamilyCom/AddAdditionalTeachers';
import { TrashIcon } from '@heroicons/react/20/solid';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { removeItemAtIndex } from '@teetsh/app/src/utils/listMethods';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';
import ButtonLink from 'components/Buttons/Link';
import { PencilIcon as Pencil } from '@heroicons/react/20/solid';
import { emptyButtonStyle } from '../EmptyButton';

interface Director {
  familyName?: string;
  givenName?: string;
  gender?: Gender;
}

interface User {
  familyName?: string;
  givenName?: string;
  gender?: Gender;
}

interface Props {
  director?: Director;
  user: User;
  loading?: boolean;
  additionalTeachers: Teacher[];
  onUpdateAdditionalTeachers?: (v: Teacher[]) => Promise<any>;
}

function getKeyGender(gender?: Gender) {
  if (gender === 'H') {
    return 'men';
  }
  if (gender === 'F') {
    return 'women';
  }
  return 'unknown';
}

function DisplayFullName({
  givenName,
  familyName,
}: {
  givenName?: string;
  familyName?: string;
}) {
  if (_isEmpty(familyName) && _isEmpty(givenName)) {
    return null;
  }
  return (
    <div>
      {!_isEmpty(familyName) && <span tw="mr-1">{familyName}</span>}
      {!_isEmpty(givenName) && <span>{givenName}</span>}
    </div>
  );
}

export function FamilyCom(props: Props) {
  const { t } = useTranslation(['carnet', 'common']);
  const {
    user,
    director,
    loading,
    additionalTeachers,
    onUpdateAdditionalTeachers,
  } = props;
  const { locale } = useUser();
  const cardStyle = tw`grid grid-cols-3 gap-x-4 min-w-[40rem]`;
  return (
    <div tw="w-full overflow-x-auto">
      <div css={cardStyle}>
        <div tw="border-t border-l border-r rounded-t-lg pt-4 px-4">
          <div>
            {t(`carnet:livret.visaTeacherLabel.${getKeyGender(user.gender)}`)}
          </div>
        </div>
        <div
          className={clsx({ 'tsh-skeleton': loading })}
          tw="border-t border-l border-r rounded-t-lg pt-4 px-4"
        >
          <div>
            {t(
              `carnet:livret.visaPrincipalLabel.${getKeyGender(
                director?.gender,
              )}`,
            )}
          </div>
        </div>
        <div tw="border-t border-l border-r rounded-t-lg pt-4 px-4">
          <div>{t('carnet:livret.parentsVisa')}</div>
        </div>
      </div>
      <div css={cardStyle}>
        <div tw="px-4 py-2 border-l border-r">
          <div tw="flex gap-2 items-center">
            <DisplayFullName
              givenName={user.givenName}
              familyName={user.familyName}
            />
            <ButtonLink
              tw="print:hidden"
              icon={Pencil}
              minimal
              small
              to="configure-user"
            />
          </div>
          {additionalTeachers.map((tt, i) => (
            <div tw="flex items-center gap-x-2 mb-2">
              <DisplayFullName
                key={tt.id}
                givenName={tt.givenName}
                familyName={tt.familyName}
              />
              {onUpdateAdditionalTeachers && (
                <div>
                  <Button
                    onClick={() =>
                      onUpdateAdditionalTeachers &&
                      onUpdateAdditionalTeachers(
                        removeItemAtIndex(additionalTeachers, i),
                      )
                    }
                    tw="print:hidden"
                    small
                    minimal
                    icon={TrashIcon}
                  />
                </div>
              )}
            </div>
          ))}
          {onUpdateAdditionalTeachers && (
            <AddAdditionalTeachers
              onUpdate={v =>
                onUpdateAdditionalTeachers([...additionalTeachers, v])
              }
            />
          )}
        </div>
        <div tw="px-4 py-2 border-l border-r">
          {director ? (
            <div tw="flex gap-2 items-center">
              <DisplayFullName
                givenName={director.givenName}
                familyName={director.familyName}
              />
              <ButtonLink
                icon={Pencil}
                tw="print:hidden"
                minimal
                small
                to="configure-director"
              />
            </div>
          ) : (
            <div>
              <ButtonLink
                minimal
                tw="print:hidden"
                small
                to="configure-director"
                css={[emptyButtonStyle, tw`w-fit`]}
              >
                {t('carnet:actions.addDirector')}
              </ButtonLink>
            </div>
          )}
        </div>
        <div tw="px-4 py-2 border-l border-r" />
      </div>
      <div css={cardStyle}>
        <div tw="px-4 pt-2 pb-1 border-l border-r">
          {t('carnet:livret.le', {
            date: format(
              new Date(),
              locale,
              t('common:datePattern.dd/MM/yyyy'),
            ),
          })}
        </div>
        <div tw="px-4 pt-2 pb-1 border-l border-r">
          {t('carnet:livret.le', {
            date: format(
              new Date(),
              locale,
              t('common:datePattern.dd/MM/yyyy'),
            ),
          })}
        </div>
        <div tw="px-4 pt-2 pb-1 border-l border-r">
          {t('carnet:livret.acknowledgeOn')}
        </div>
      </div>
      <div css={cardStyle}>
        {[0, 1, 2].map(tt => (
          <div
            key={tt}
            tw="bg-neutral-supershy h-40 p-4 border-b border-l border-r rounded-b-lg"
          >
            {t('carnet:livret.signature')}
          </div>
        ))}
      </div>
    </div>
  );
}
